import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs, Modal, Dropdown, MenuProps } from 'antd';
import { Link, useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import * as _ from 'lodash';
import { FiChevronLeft, FiShare2 } from 'react-icons/fi';
import { parseISO } from 'date-fns';
import Countdown from 'react-countdown';
import { IoTrophy } from 'react-icons/io5';
import { FaMedal } from 'react-icons/fa';

import { CheckCircleOutlined } from '@ant-design/icons';
import { useBullet } from '@/context/BulletContext';
import { useShare } from '@/context/ShareContext';
import { UploadRequests } from '@/services/api/requests/Upload';
import CustomAntButton from '../../../components/CustomAntButton';

import Ranking from './Ranking';
import MyGames from './MyGames';

import {
  Container,
  Header,
  AccumulatedValueContainer,
  RestTimerContainer,
  Content,
  ContentTab,
  ItemPriceDropdown,
} from './styles';

import api from '../../../services/api';
import socket from '../../../services/socket';
import { formatMoney } from '../../../utils/formatters';

import Loading from '../../../components/Loading';
import NotFound from '../../../components/NotFound';
import { showToast } from '../../../hooks/showToast';
import { useAuth } from '../../../context/AuthContext';
import { useChat } from '../../../context/ChatContext';
import { useIntl } from '../../../context/IntlContext';

import { ECardTypeEnum, ICardGame } from '../types';

import {
  IRouteParams,
  IMyGame,
  IRankingPosition,
  IRankingSearchUser,
} from './types';
import Results from './Results';

const { TabPane } = Tabs;

const { confirm } = Modal;

const CardGame: React.FC = () => {
  const { search: searchLocation } = useLocation();
  const searchParams = new URLSearchParams(searchLocation);
  const influencerCodeByParams = searchParams.get('influencer_code');

  const prepareTimeInMin = 10;
  const prepareTimeInMs = prepareTimeInMin * 60000;

  const { url, params } = useRouteMatch<IRouteParams>();
  const history = useHistory();

  const intl = useIntl();
  const { user: me } = useAuth();
  const { setChat } = useChat();
  const { openShareAssistant } = useShare();
  const bullet = useBullet();

  const [isInfluencerOnCard, setIsInfluencerOnCard] = useState(false);

  const [cardGame, setCardGame] = useState<ICardGame | null>(null);
  const [loadingCard, setLoadingCard] = useState(true);

  const [ranking, setRanking] = useState<IRankingPosition[]>([]);
  const [loadingRanking, setLoadingRanking] = useState(true);
  const [paginationRanking, setPaginationRanking] = useState({
    currentPage: 1,
    totalPages: 0,
  });

  const [findedOnSearchUsers, setFindedOnSearchUsers] = useState<
    IRankingSearchUser[]
  >([]);
  const [searchUserValue, setSearchUserValue] = useState('');
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [paginationSearchUser, setPaginationSearchUser] = useState({
    currentPage: 1,
    totalPages: 0,
  });
  const [userSelectedOnSearch, setUserSelectedOnSearch] =
    useState<IRankingSearchUser | null>(null);
  const [loadingUserSelectedOnSearch, setLoadingUserSelectedOnSearch] =
    useState(false);
  const [
    paginationGamesUserSelectedOnSearch,
    setPaginationGamesUserSelectedOnSearch,
  ] = useState({
    currentPage: 1,
    totalPages: 0,
  });

  const [myGames, setMyGames] = useState<IMyGame[]>([]);
  const [loadingMyGames, setLoadingMyGames] = useState(false);
  const [myGamesPagination, setMyGamesPagination] = useState({
    currentPage: 1,
    totalPages: 0,
  });

  const [currentView, setCurrentView] = useState<
    'ranking' | 'results' | 'myGames'
  >('myGames');

  const [itemsPrizePlacement, setItemsPrizePlacement] = useState<
    MenuProps['items']
  >([]);

  const getRanking = useCallback(
    async (page = 1): Promise<void> => {
      setLoadingRanking(true);
      try {
        const { data } = await api.get<{
          docs: IRankingPosition[];
          page: number;
          pages: number;
        }>('/api/ranking-athlete', {
          params: {
            card: params.card_game_id,
            limit: 10,
            page,
          },
        });

        if (page === 1) {
          setRanking([
            ...data.docs.map(rankPosition => {
              return {
                ...rankPosition,
                _user: {
                  ...rankPosition?._user,
                  isMe: me?._id === rankPosition?._user?._id,
                },
              };
            }),
          ]);
        } else {
          setRanking(oldRanking => [
            ...oldRanking,
            ...data.docs.map(rankPosition => {
              return {
                ...rankPosition,
                _user: {
                  ...rankPosition?._user,
                  isMe: me?._id === rankPosition?._user?._id,
                },
              };
            }),
          ]);
        }

        setPaginationRanking({
          currentPage: data.page,
          totalPages: data.pages,
        });
      } catch (error) {
        showToast({
          type: 'error',
          title: intl.getTranslatedText(
            'pages.athletesOfTheWeek.card.messages.getRankingError.title',
          ),
          description: intl.getTranslatedText(
            'common.errors.unexpectedError.description',
          ),
        });
      }
      setLoadingRanking(false);
    },
    [intl, me?._id, params.card_game_id],
  );

  const searchUserOnRanking = useCallback(
    async (search: string, page = 1): Promise<void> => {
      setLoadingSearch(true);
      try {
        const { data } = await api.get<{
          docs: IRankingSearchUser[];
          page: number;
          pages: number;
        }>('/api/user', {
          params: {
            search,
            page,
            limit: 6,
          },
        });

        setFindedOnSearchUsers(
          data.docs.map(user => {
            return {
              ...user,
              isMe: me?._id === user?._id,
              games: [],
            };
          }),
        );

        setPaginationSearchUser({
          currentPage: data.page,
          totalPages: data.pages,
        });
      } catch (error) {
        showToast({
          type: 'error',
          title: intl.getTranslatedText('common.errors.unexpectedError.title'),
          description: intl.getTranslatedText(
            'common.errors.unexpectedError.description',
          ),
        });
      }
      setLoadingSearch(false);
    },
    [intl, me?._id],
  );

  const handleGetUserSelected = useCallback(
    async (value: string, page = 1): Promise<void> => {
      if (page === 1) {
        setUserSelectedOnSearch(null);
        setSearchUserValue(value);
      }

      const user = findedOnSearchUsers.find(
        findedUser => findedUser.username === value,
      );

      if (!user) return;

      setLoadingUserSelectedOnSearch(true);
      try {
        const { data } = await api.get<{
          docs: IRankingPosition[];
          page: number;
          pages: number;
        }>('/api/ranking-athlete', {
          params: {
            card: params.card_game_id,
            limit: 6,
            user: user._id,
            page,
          },
        });

        if (page === 1) {
          user.games = data.docs;
        } else {
          user.games = [...user.games, ...data.docs];
        }

        setUserSelectedOnSearch(user);
        setPaginationGamesUserSelectedOnSearch({
          currentPage: data.page,
          totalPages: data.pages,
        });
      } catch (error) {
        showToast({
          type: 'error',
          title: intl.getTranslatedText('common.errors.unexpectedError.title'),
          description: intl.getTranslatedText(
            'common.errors.unexpectedError.description',
          ),
        });
      }
      setLoadingUserSelectedOnSearch(false);
    },
    [findedOnSearchUsers, intl, params.card_game_id],
  );

  const getMyGames = useCallback(
    async (page = 1): Promise<void> => {
      setLoadingMyGames(true);
      try {
        const { data } = await api.get<{
          docs: IMyGame[];
          page: number;
          pages: number;
        }>('/api/played-athlete', {
          params: {
            card: params.card_game_id,
            limit: 6,
            page,
          },
        });

        setMyGames(oldState => {
          const lastPagesGames = page > 1 ? oldState : [];
          return [...lastPagesGames, ...data.docs];
        });
        setMyGamesPagination({
          currentPage: data.page,
          totalPages: data.pages,
        });
      } catch (error) {
        showToast({
          type: 'error',
          title: intl.getTranslatedText(
            'pages.athletesOfTheWeek.card.messages.getMyGamesError.title',
          ),
          description: intl.getTranslatedText(
            'common.errors.unexpectedError.description',
          ),
        });
      }
      setLoadingMyGames(false);
    },
    [intl, params.card_game_id],
  );

  const getAthleteCardGame = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<{
        doc: Omit<ICardGame, 'amount' | 'cardCount'>;
        amount: number;
        cardCount: number;
      }>(`/api/athlete-card/${params.card_game_id}`);

      const athletesWithAbbrevOnTeams = data.doc.athletes.map(athlete => ({
        ...athlete,
        _team: {
          ...athlete._team,
          abbrev: athlete._team.name.substring(0, 3).toUpperCase(),
        },
      }));

      setCardGame({
        ...data.doc,
        athletes: athletesWithAbbrevOnTeams,
        amount: data.amount,
        cardCount: data.cardCount,
      });

      if (data.doc.cardType !== ECardTypeEnum.NEW) {
        setCurrentView('ranking');
        getRanking();
      }
      setChat({
        _resourceId: data.doc._id,
        name: data.doc.name,
        from: intl.getTranslatedText('common.gamesTitles.athletesOfTheWeek'),
        fromCode: 'athletesCard',
      });
    } catch (error) {
      // showToast({
      //   type: 'error',
      //   title: 'Ocorreu um erro ao carregar os campeonatos',
      //   description: 'Recarregue a página!',
      // });
    }
    setLoadingCard(false);
  }, [getRanking, intl, params.card_game_id, setChat]);

  const checkIfIsInfluencerOnCard = useCallback(async (): Promise<void> => {
    try {
      const { data: isInfluencerCodeValid } = await api.get(
        `/api/athlete-card/${params.card_game_id}/check-influencer-code/${me?.code}`,
      );

      setIsInfluencerOnCard(isInfluencerCodeValid);
    } catch {
      showToast({
        type: 'error',
        title: intl.getTranslatedText('common.errors.unexpectedError.title'),
        description: intl.getTranslatedText(
          'common.errors.unexpectedError.description',
        ),
      });
    }
  }, [intl, me?.code, params.card_game_id]);

  const handlePriceDropdownItems = useCallback(() => {
    if (cardGame) {
      const contentStyle: React.CSSProperties = {
        cursor: 'default',
        padding: 0,
        transition: 'none',
      };

      setItemsPrizePlacement([
        {
          key: '0',
          label: (
            <ItemPriceDropdown $medalType="gold">
              <IoTrophy size={18} />
              {intl.getTranslatedText('pages.athletesOfTheWeek.amount', {
                price: formatMoney(cardGame?.amount),
              })}
            </ItemPriceDropdown>
          ),
          style: contentStyle,
        },
        {
          type: 'divider',
        },
        {
          key: '1',
          label: (
            <ItemPriceDropdown $medalType="gold">
              <FaMedal size={18} />
              {intl.getTranslatedText('pages.athletesOfTheWeek.placement.1', {
                price: formatMoney(
                  cardGame?.amount * (cardGame?.paymentPercentage.first / 100),
                ),
              })}
            </ItemPriceDropdown>
          ),
          style: contentStyle,
        },
        {
          key: '2',
          label: (
            <ItemPriceDropdown $medalType="silver">
              <FaMedal size={18} />
              {intl.getTranslatedText('pages.athletesOfTheWeek.placement.2', {
                price: formatMoney(
                  cardGame?.amount * (cardGame?.paymentPercentage.second / 100),
                ),
              })}
            </ItemPriceDropdown>
          ),
          style: contentStyle,
        },
        {
          key: '3',
          label: (
            <ItemPriceDropdown $medalType="bronze">
              <FaMedal size={18} />
              {intl.getTranslatedText('pages.athletesOfTheWeek.placement.3', {
                price: formatMoney(
                  cardGame?.amount * (cardGame?.paymentPercentage.third / 100),
                ),
              })}
            </ItemPriceDropdown>
          ),
          style: contentStyle,
        },
      ]);
    }
  }, [cardGame, intl]);

  useEffect(() => {
    getAthleteCardGame();

    const isInfluencer = me?.roles.includes('influencer') && !!me?.code;
    if (isInfluencer) {
      checkIfIsInfluencerOnCard();
    }

    return () => {
      setChat(null);
    };
  }, [checkIfIsInfluencerOnCard, getAthleteCardGame, me, setChat]);

  useEffect(() => {
    socket.on(
      `athlete:${params.card_game_id}`,
      (data: { status: 'new_game' }) => {
        if (data.status === 'new_game') {
          setCardGame(oldCardGame => {
            if (oldCardGame) {
              return {
                ...oldCardGame,
                cardCount: oldCardGame.cardCount + 1,
                amount: oldCardGame.fixedAmount
                  ? oldCardGame.fixedAmount
                  : oldCardGame.amount + oldCardGame.price,
              };
            }
            return null;
          });
        }
      },
    );

    socket.on(`athlete-end:${params.card_game_id}`, () => {
      const randomTime = Math.random() * (2000 - 500) + 500;

      setTimeout(() => {
        getAthleteCardGame();
        getMyGames();
      }, randomTime);
    });

    return () => {
      socket.off(`athlete:${params.card_game_id}`);
      socket.off(`athlete-end:${params.card_game_id}`);
    };
  }, [getAthleteCardGame, getMyGames, params.card_game_id]);

  useEffect(() => {
    if (currentView === 'myGames' && myGames.length === 0) {
      getMyGames();
    }

    if (!itemsPrizePlacement?.length) {
      handlePriceDropdownItems();
    }
  }, [
    currentView,
    handlePriceDropdownItems,
    getMyGames,
    intl,
    itemsPrizePlacement?.length,
    myGames.length,
  ]);

  const usersSearchDebounced = useMemo(() => {
    return _.debounce(searchUserOnRanking, 500);
  }, [searchUserOnRanking]);

  const handleSearchUser = useCallback(
    (value: string) => {
      usersSearchDebounced.cancel();

      if (userSelectedOnSearch) {
        setUserSelectedOnSearch(null);
      }
      setSearchUserValue(value);

      if (value.length > 3) {
        setLoadingSearch(true);
        usersSearchDebounced(value);
      } else {
        setLoadingSearch(false);
        setFindedOnSearchUsers([]);
      }
    },
    [userSelectedOnSearch, usersSearchDebounced],
  );

  const handleGoToPlay = useCallback(async () => {
    await new Promise(resolve => {
      const content = cardGame?.price
        ? intl.getTranslatedText(
            'pages.athletesOfTheWeek.card.messages.goToPlayConfirm.description',
            {
              price: formatMoney(cardGame.price),
            },
          )
        : intl.getTranslatedText(
            'pages.athletesOfTheWeek.card.messages.goToPlayConfirm.freeCardDescription',
          );

      confirm({
        title: intl.getTranslatedText(
          'pages.athletesOfTheWeek.card.messages.goToPlayConfirm.title',
        ),
        icon: <CheckCircleOutlined />,
        content,
        cancelText: intl.getTranslatedText('common.buttons.cancel'),
        okText: intl.getTranslatedText(
          'pages.athletesOfTheWeek.card.goToPlayConfirmButton',
        ),
        onOk() {
          resolve(true);
        },
      });
    });

    if (cardGame?.price) {
      const hasAvailableBalance = bullet.checkIfBalanceIsAvailableToAction(
        cardGame.price,
      );
      if (!hasAvailableBalance) return;
    }

    history.push(
      `${url}/play${
        influencerCodeByParams
          ? `?influencer_code=${influencerCodeByParams}`
          : ''
      }`,
    );
  }, [bullet, cardGame?.price, history, influencerCodeByParams, intl, url]);

  if (loadingCard) {
    return (
      <Container>
        <Loading />
      </Container>
    );
  }

  if (!cardGame) {
    return (
      <NotFound>
        <h4>
          {intl.getTranslatedTextWithHTML(
            'pages.athletesOfTheWeek.card.messages.noCardGame',
          )}
        </h4>
        <Link to="/">
          <FiChevronLeft size={16} />
          {intl.getTranslatedText(
            'pages.athletesOfTheWeek.card.goToAthletesOfTheWeekLink',
          )}
        </Link>
      </NotFound>
    );
  }

  return (
    <Container>
      <Header
        $bg={cardGame.banner && UploadRequests.getFileUrl(cardGame.banner)}
      >
        {(cardGame.cardType === ECardTypeEnum.NEW ||
          cardGame.cardType === ECardTypeEnum.PREPARING) && (
          <Countdown
            date={parseISO(cardGame.startDate)}
            renderer={({ total, days, hours, minutes, seconds, completed }) => {
              if (completed) {
                return <></>;
              }

              if (
                total <= prepareTimeInMs &&
                cardGame.cardType === ECardTypeEnum.NEW
              ) {
                setCardGame(oldCardGame => {
                  if (oldCardGame) {
                    setCurrentView('ranking');
                    getRanking();
                    return {
                      ...oldCardGame,
                      cardType: ECardTypeEnum.PREPARING,
                    };
                  }
                  return null;
                });
              }

              return (
                <RestTimerContainer>
                  <div>
                    <h6>
                      {days.toLocaleString(undefined, {
                        minimumIntegerDigits: 2,
                        useGrouping: false,
                      })}
                    </h6>
                    <small>
                      {' '}
                      {intl.getTranslatedText('common.countDown.days')}
                    </small>
                  </div>
                  <div>
                    <h6>
                      {hours.toLocaleString(undefined, {
                        minimumIntegerDigits: 2,
                        useGrouping: false,
                      })}
                    </h6>
                    <small>
                      {' '}
                      {intl.getTranslatedText('common.countDown.hours')}
                    </small>
                  </div>
                  <div>
                    <h6>
                      {minutes.toLocaleString(undefined, {
                        minimumIntegerDigits: 2,
                        useGrouping: false,
                      })}
                    </h6>
                    <small>
                      {intl.getTranslatedText('common.countDown.minutes')}
                    </small>
                  </div>
                  <div>
                    <h6>
                      {seconds.toLocaleString(undefined, {
                        minimumIntegerDigits: 2,
                        useGrouping: false,
                      })}
                    </h6>
                    <small>
                      {intl.getTranslatedText('common.countDown.seconds')}
                    </small>
                  </div>
                </RestTimerContainer>
              );
            }}
            onComplete={() => {
              setLoadingCard(true);
              getAthleteCardGame();
            }}
          />
        )}
        <CustomAntButton
          type="text"
          useCustomIcon
          icon={<FiShare2 size={21} />}
          onClick={() =>
            openShareAssistant({
              url: isInfluencerOnCard
                ? `${window.location.href}?influencer_code=${me?.code}`
                : window.location.href,
            })
          }
        />
        <div>
          <div>
            <h6>{cardGame.name}</h6>
          </div>
          <div>
            <Dropdown
              menu={{ items: itemsPrizePlacement }}
              trigger={['click', 'hover']}
              arrow
            >
              <AccumulatedValueContainer>
                <IoTrophy size={18} />
                <small>R$ {formatMoney(cardGame.amount)}</small>
              </AccumulatedValueContainer>
            </Dropdown>

            {cardGame.cardType === ECardTypeEnum.NEW && (
              <CustomAntButton onClick={handleGoToPlay} type="primary">
                {intl.getTranslatedText(
                  'pages.athletesOfTheWeek.card.playNowButton',
                )}
              </CustomAntButton>
            )}
          </div>
        </div>
      </Header>
      <Content>
        <div>
          {cardGame.cardType === ECardTypeEnum.NEW && (
            <CustomAntButton onClick={handleGoToPlay} type="primary">
              {intl.getTranslatedText(
                'pages.athletesOfTheWeek.card.playNowButton',
              )}
            </CustomAntButton>
          )}
        </div>
        <Tabs
          activeKey={currentView}
          onTabClick={tabKey => {
            if (
              tabKey === 'ranking' ||
              tabKey === 'results' ||
              tabKey === 'myGames'
            ) {
              setCurrentView(tabKey);
            }
          }}
          centered
        >
          {cardGame.cardType !== ECardTypeEnum.NEW && (
            <>
              <TabPane
                tab={intl.getTranslatedText(
                  'pages.athletesOfTheWeek.card.tabs.ranking',
                )}
                key="ranking"
              >
                <ContentTab>
                  <Ranking
                    ranking={ranking}
                    loading={loadingRanking}
                    cardType={cardGame.cardType}
                    loadMoreRanking={() =>
                      getRanking(paginationRanking.currentPage + 1)
                    }
                    isOnLastPageOfRanking={
                      paginationRanking.currentPage ===
                      paginationRanking.totalPages
                    }
                    handleSearchUser={handleSearchUser}
                    searchUserValue={searchUserValue}
                    loadingSearchUsers={loadingSearch}
                    findedOnSearchUsers={findedOnSearchUsers}
                    isOnLastPageOfSearchUsers={
                      paginationSearchUser.currentPage ===
                      paginationSearchUser.totalPages
                    }
                    loadMoreSearchUsers={() =>
                      searchUserOnRanking(
                        searchUserValue,
                        paginationSearchUser.currentPage + 1,
                      )
                    }
                    handleGetUserSelected={handleGetUserSelected}
                    userSelectedOnSearch={userSelectedOnSearch}
                    isOnLastPageOfGamesUserSelectedOnSearch={
                      paginationGamesUserSelectedOnSearch.currentPage ===
                      paginationGamesUserSelectedOnSearch.totalPages
                    }
                    loadMoreGamesUserSelectedOnSearch={() =>
                      handleGetUserSelected(
                        userSelectedOnSearch?.username || '',
                        paginationGamesUserSelectedOnSearch.currentPage + 1,
                      )
                    }
                    loadingUserSelectedOnSearch={loadingUserSelectedOnSearch}
                    myGames={myGames}
                    setCurrentView={setCurrentView}
                  />
                </ContentTab>
              </TabPane>
              <TabPane
                tab={intl.getTranslatedText(
                  'pages.athletesOfTheWeek.card.tabs.results',
                )}
                key="results"
              >
                <ContentTab>
                  <Results cardGame={cardGame} />
                </ContentTab>
              </TabPane>
            </>
          )}
          <TabPane
            tab={intl.getTranslatedText(
              'pages.athletesOfTheWeek.card.tabs.myGames',
            )}
            key="myGames"
          >
            <ContentTab>
              <MyGames
                myGames={myGames}
                loading={loadingMyGames}
                loadMore={() => getMyGames(myGamesPagination.currentPage + 1)}
                isOnLastPage={
                  myGamesPagination.currentPage === myGamesPagination.totalPages
                }
                cardType={cardGame.cardType}
              />
            </ContentTab>
          </TabPane>
        </Tabs>
      </Content>
    </Container>
  );
};

export default CardGame;
